import { useEffect, useState } from 'react';
import { MESSAGE_BUS_CALLBACK_INTERVAL, MESSAGE_BUS_LAST_ID, MESSAGE_BUS_SCRIPT_EXISTS_POLL_COUNT, LOG } from '../Constants';
import { useMessageBusCallbacks } from './useMessageBusCallbacks';
import { useMessageBusState, useMessageBusDispatch } from '../../context/messagebus';
import { useLog } from './useLog';
import { clone, stringify } from '../utils/helpers';
const useMessageBus = props => {
  const {
    log
  } = useLog();
  const messageBusDispatch = useMessageBusDispatch();
  const messageBusState = useMessageBusState();
  const {
    newHandler,
    notificationAlertHandler,
    latestHandler
  } = useMessageBusCallbacks();
  const [isInitialized, setIsInitialized] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const {
    userAuthenticated
  } = props;
  const win = window;
  useEffect(() => {
    if (isListening) log(`${LOG.bullet} MessageBus is listening`);
  }, [isListening]);
  useEffect(() => {
    log(`${LOG.bullet} MessageBus state`, stringify(messageBusState, true));
  }, [messageBusState]);
  useEffect(() => {
    if (win.MessageBus) {
      log(`${LOG.bullet} win.MessageBus`, win.MessageBus);
    } else {
      log(`${LOG.bullet} win.MessageBus`, win.MessageBus);
    }
  }, [win.MessageBus]);

  // helpers, utils
  const delay = () => {
    return new Promise(resolve => setTimeout(resolve, 1));
  };
  const pollForNext = (pollRoutine, count) => {
    if (count && count <= MESSAGE_BUS_SCRIPT_EXISTS_POLL_COUNT) {
      log(`${LOG.whiteBullet} poll count: ${count}`);
      pollRoutine();
    } else if (count && count < MESSAGE_BUS_SCRIPT_EXISTS_POLL_COUNT) {
      log(`${LOG.whiteBullet} poll max reached - object not found`);
    }
  };
  const pollForObject = async (obj, hasObject, setHasObject, count, setHasCount) => {
    if (!hasObject) {
      if (obj) {
        setHasObject(true);
      } else {
        await delay();
        setHasCount(count + 1);
      }
    }
  };

  // async management - mounted, hasWindow, hasMessageBus
  useEffect(() => {
    log(`${LOG.bullet} useMessageBus`, 'mounted');
    setIsMounted(true);
  }, []);
  const [isMounted, setIsMounted] = useState(false);
  useEffect(() => {
    if (isMounted) {
      log(`${LOG.bullet} useMessageBus hook is mounted`);
      pollForHasWindow();
    }
  }, [isMounted]);

  /**
   * has window
   */
  const [hasWindow, setHasWindow] = useState(false);
  const [hasWindowPollCount, setHasWindowPollCount] = useState(0);
  const pollForHasWindow = async () => {
    await pollForObject(win, hasWindow, setHasWindow, hasWindowPollCount, setHasWindowPollCount);
  };
  useEffect(() => {
    pollForNext(pollForHasWindow, hasWindowPollCount);
  }, [hasWindowPollCount]);
  useEffect(() => {
    if (hasWindow) {
      log(`${LOG.bullet} window available`);
      pollForMessageBus();
    }
  }, [hasWindow]);

  /**
   * MessageBus
   */
  const [messageBus, setMessageBus] = useState(null);
  const [hasMessageBus, setHasMessageBus] = useState(false);
  const [hasMessageBusPollCount, setHasMessageBusPollCount] = useState(0);
  const pollForMessageBus = async () => {
    await pollForObject(win.MessageBus, hasMessageBus, setHasMessageBus, hasMessageBusPollCount, setHasMessageBusPollCount);
  };
  useEffect(() => {
    pollForNext(pollForMessageBus, hasMessageBusPollCount);
  }, [hasMessageBusPollCount]);
  useEffect(() => {
    console.log(`${LOG.bullet} useMessageBus`, 'hasMessageBus', hasMessageBus);
    if (hasMessageBus) {
      setMessageBus(win.MessageBus);
      log(`${LOG.whiteBullet} MessageBus found`);
    }
  }, [hasMessageBus]);
  useEffect(() => {
    if (messageBus) {
      log(`${LOG.whiteBullet} MessageBus available`, messageBus);
    }
  }, [messageBus]);
  useEffect(() => {
    if (userAuthenticated) {
      if (hasMessageBus) {
        log(`${LOG.check} 3. MessageBus ready`);
        log(`${LOG.check} 4. do MessageBus stuff`);
        setIsInitialized(true);
      }
    }
  }, [userAuthenticated, hasMessageBus]);
  useEffect(() => {
    if (isInitialized) {
      log(`${LOG.bullet} MessageBus is initialized`);
      setIsListening(true);
    }
  }, [isInitialized]);
  const startPollingForMessages = options => {
    try {
      // get MessageBus, set interval, url, headers
      const mb = messageBus;
      mb.callbackInterval = MESSAGE_BUS_CALLBACK_INTERVAL;
      mb.baseUrl = options.baseUrl;
      mb.baseUrl += mb.baseUrl.slice(-1) == '/' ? '' : '/';
      mb.headers = {
        'User-Api-Key': options.userApiKey,
        'Discourse-Present': true
      };

      // logs: delete this block
      log(`${LOG.bullet} MessageBus`, mb);
      log(`${LOG.bullet} MessageBus baseUrl ${mb.baseUrl}`);
      log(`${LOG.bullet} MessageBus mb.headers`, mb.headers);
      log(`${LOG.bullet} MessageBus mb.callbackInterval`, mb.callbackInterval);

      //  start and subscribe to MessageBus
      mb.start();

      // subscriptions
      let slug = `/notification-alert/${options.discourseUserId}`;
      log(`${LOG.bullet} MessageBus subscribe`, slug);
      mb.subscribe(slug, function (data) {
        notificationAlertHandler(data);
      }, MESSAGE_BUS_LAST_ID.get_all_new_messages);
      slug = `/new`;
      log(`${LOG.bullet} MessageBus subscribe`, slug);
      mb.subscribe(slug, function (data) {
        newHandler(data);
      });
      slug = '/latest';
      log(`${LOG.bullet} MessageBus subscribe`, slug);
      mb.subscribe(slug, function (data) {
        latestHandler(data);
      });
    } catch (e) {
      console.log(`${LOG.crossMark}`, e);
    }
  };

  // context management
  const closeReplyBanner = id => {
    const repliesClone = clone(messageBusState.replies);
    const updatedReplies = repliesClone.filter(reply => reply.data.topic_id !== id);
    messageBusDispatch({
      type: 'UPDATE_REPLIES',
      payload: updatedReplies
    });
  };
  const closeTopicBanner = () => {
    refreshTopics();
  };
  const refreshTopics = () => {
    messageBusDispatch({
      type: 'INIT_TOPICS'
    });
    messageBusDispatch({
      type: 'INIT_REPLIES'
    });
  };
  const refreshTopic = () => {
    messageBusDispatch({
      type: 'INIT_REPLIES'
    });
  };
  const findTopicIdForType = (list, id, type) => {
    const itemsWithId = list.filter(i => i.data.topic_id === id && i.data.message_type === type);
    return itemsWithId.length;
  };
  const handleMyEvents = () => {
    const events = messageBusState.events;
    if (events.length) {
      // state object containing clones of stored context data items
      const state = clone(messageBusState);
      const event = state.events.shift(); // remove and return first item in array
      if (eventMatchesFound(event, state)) {
        deleteMatches(event, state);
        messageBusDispatch({
          type: 'SET_UPDATE_IN_PROGRESS',
          payload: false
        });
      }
    }
  };
  const eventMatchesFound = (event, state) => {
    const matchCount = event.deactivate.length + 1;
    let eventsFoundCount = 0;

    // look for topic id in all replies for event
    event.deactivate.forEach(item => {
      const allRepliesTopics = state[item.whichList];
      if (findTopicIdForType(allRepliesTopics, event.topic_id, item.message_type)) {
        eventsFoundCount++;
      }
    });
    const myEvents = state.my[event.type];
    if (findTopicIdForType(myEvents, event.topic_id, event.dispatch_type)) {
      eventsFoundCount++;
    }
    const matchesFound = matchCount === eventsFoundCount;
    return matchesFound;
  };
  const deleteMatches = (event, state) => {
    event.deactivate.forEach(item => {
      state[item.whichList] = state[item.whichList].filter(e => !(e.data.topic_id === event.topic_id && e.data.message_type === item.message_type));
    });

    // event.dispatch_type: 'ADD_ITEM_TO_MY_TOPICS' : 'ADD_ITEM_TO_MY_REPLIES';
    state.my[event.type] = state.my[event.type].filter(e => !(e.data.topic_id === event.topic_id && e.data.message_type === event.dispatch_type));
    messageBusDispatch({
      type: 'UPDATE_INITAL_STATE',
      payload: state
    });
  };
  useEffect(() => {
    handleMyEvents();
  }, [messageBusState.events, messageBusState.replies, messageBusState.topics, messageBusState.my]);
  const getUniqueList = list => {
    const unique = arr => [...new Set(arr)];
    let idList = [];
    if (!messageBusState.update_in_progress) {
      const listClone = clone(list);
      idList = listClone.map(e => e.data.topic_id);
    }
    return unique(idList);
  };

  // return {hasMessageBus, doMessageBusStuff};
  return {
    isListening,
    isInitialized,
    closeReplyBanner,
    closeTopicBanner,
    refreshTopic,
    refreshTopics,
    startPollingForMessages,
    getUniqueList
  };
};
export { useMessageBus };
//# sourceMappingURL=useMessageBus.js.map